<template>
    <div class="page-404-bg">
        <div class="wrapper-pages pt-2 pt-sm-5 pb-4 pb-sm-5 position-relative">
            <div class="container">
                <div class="row justify-content-center">
                    <div class="col-xxl-4 col-lg-5">
                        <div class="brand-logo mb-5 text-center">
                            <a href="index.html">
                                <router-link to="/login">
                                    <img :src="`${$logoDir}logo-auth.png`" alt="HAXO" height="72" class="img-fluid">
                                </router-link>
                            </a>
                        </div>

                        <div class="card">
                            <div class="card-body p-4">
                                <div class="text-center">
                                    <h1 class="text-error">{{ $t('common.welcome') }}</h1>
                                    <p class="text-white mt-3">{{ $t('installerValidation.subtitle') }}</p>
                                </div>
                                <form @submit.prevent="handleSubmit" class="pb-4" autocomplete="off">
                                    <div class="my-auto text-white mb-3">
                                        <div class="mb-2">
                                            <PasswordComponent id="password" :label="$t('changepwd.newPassword')" v-model="password" :required="false" />
                                        </div>
                                        <div class="mb-2">
                                            <PasswordComponent id="confirmed-password" :label="$t('changepwd.confirmPassword')" v-model="confirmed_password" :required="false" />
                                        </div>
                                        <div class="mb-2">
                                            <label for="mfa" class="form-label">{{ $t('account.mfaMethod') }}</label>
                                            <select class="form-select" v-model="mfa" required>
                                                <option value="none">{{ $t('account.mfa.passwordOnlyNotRecommended') }}</option>
                                                <option value="email">{{ $t('account.mfa.passwordEmail') }}</option>
                                                <option value="sms">{{ $t('account.mfa.passwordSMS') }}</option>
                                                <option value="totp">{{ $t('account.mfa.passwordApp') }}</option>
                                            </select>
                                        </div>
                                        <div v-if="mfa === 'totp'" class="mb-2">
                                            <div class="text-center">
                                                <p>{{ $t('installerValidation.qrCodeInstructions') }}</p>
                                                <qrcode-vue :size="200" :value="qrcode"></qrcode-vue>
                                            </div>
                                            <div class="mb-2">
                                                <PasswordComponent label="Secret" :required="false" :readonly="true" v-model="totpSecret" />
                                                <label class="mt-2 form-label">Code</label>
                                                <input class="form-control" type="code" required :placeholder="$t('mfa.codePlaceholder')" v-model="totpCode">
                                            </div>
                                        </div>
                                        <div class="mb-2">
                                            <div class="form-check">
                                                <input type="checkbox" v-model="cguAccepted" class="form-check-input" id="checkbox-cgu">
                                                {{ $t('common.termsAgreement') }} <a :href="cguLink" target="_blank" rel="noopener noreferrer" style="color: white; text-decoration: underline;">{{ $t('common.termsOfUse') }}</a>
                                            </div>
                                        </div>
                                    </div>
                                    <div class="d-grid mb-0 text-center">
                                        <button class="btn btn-info" type="submit" :disabled="!cguAccepted"><i class="mdi mdi-check"></i> {{ $t('common.submit') }}</button>
                                    </div>
                                    <div v-if="errorMessage" class="mt-2 text-white">
                                        {{ errorMessage }}
                                    </div>
                                </form>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </div>

        <footer class="footer footer-alt text-white">
            {{ year }} {{ $t('common.copyright') }}
        </footer>
    </div>
    <MfaModal ref="mfaModal" :action="validateInstallerAccount" />
</template>

<script setup>
import { ref, onMounted } from 'vue';
import { useRoute, useRouter } from 'vue-router';
import { useUserStore } from '@/stores/userStore';
import { useI18n } from 'vue-i18n';
import PasswordComponent from '@/components/password';
import MfaModal from '@/modals/mfaModal.vue';
import QrcodeVue from 'qrcode.vue';
import base32 from 'base32.js';

const cguLink = ref(process.env.VUE_APP_CGU_URL);
const router = useRouter();
const route = useRoute();
const userStore = useUserStore();
const { t } = useI18n();
const uuid = route.query.uuid;
const year = new Date().getFullYear();
const password = ref('');
const confirmed_password = ref('');
const mfa = ref('none');
const cguAccepted = ref(false);
const totpCode = ref(null);
const totpSecret = ref(null);
const secret = ref(null);
const qrcode = ref(null);
const mfaModal = ref(null);
const errorMessage = ref(null);

onMounted(async () => {
    try {
        const response = await userStore.checkUUID(uuid, 'new_installer');
        const email = response.email;
        const randomBytes = new Uint8Array(20);
        window.crypto.getRandomValues(randomBytes);
        secret.value = Array.from(randomBytes, byte => byte.toString(16).padStart(2, '0')).join('').toUpperCase();
        totpSecret.value = base32.encode(randomBytes);
        qrcode.value = `otpauth://totp/HaxoCloud:${email}?secret=${totpSecret.value}&issuer=Haxo`;
    } catch (error) {
        router.push({ path: "/result", query: { success: false, title: t('common.failure'), message: error }});
    }
});

async function handleSubmit() {
    if(validate()) {
        await validateInstallerAccount();
    }
}

function validate() {
    const passwordRegex = /^(?=.*[a-z])(?=.*[A-Z])(?=.*\d)(?=.*[@$!%*?&])[A-Za-z\d@$!%*?&]{8,}$/;
    if (!passwordRegex.test(password.value)) {
        displayError('password', t('common.passwordRequirements'));
        return false;
    }
    if(password.value != confirmed_password.value) {
        displayError('confirmed-password', t('common.passwordsMustMatch'));
        return false;
    }
    return true;
}

function displayError(id, error) {
    const input = document.getElementById(id);
    input.setCustomValidity(error);
    input.reportValidity();
}

async function validateInstallerAccount(code) {
    errorMessage.value = "";
    try {
        const response = await userStore.validateInstaller(uuid, password.value, mfa.value, totpSecret.value, totpCode.value, code, cguAccepted.value);
        if(response == 'ok') {
            router.push({ path: "/result", query: { success: true, title: t('adminCreationSuccess.title'), message: t('installerValidation.successMessage') }});
        } else {
            mfaModal.value.show(userStore.mfa_info.message);
        }
    } catch(error) {
        errorMessage.value = error;
    }
}

</script>

<style scoped>
.page-404-bg {
    background: rgb(45, 64, 140);
    background: linear-gradient(164deg, rgb(32, 123, 194) 0%, rgb(45, 64, 140) 50%, rgb(114, 44, 132) 100%);
}

.page-404-bg .wrapper-pages {
    -webkit-box-align: center;
    -ms-flex-align: center;
    align-items: center;
    display: -webkit-box;
    display: -ms-flexbox;
    display: flex;
    min-height: 100vh;
}

.page-404-bg .card {
    background: rgba(255, 255, 255, 0.11);
    border-radius: 16px;
    -webkit-box-shadow: 0 4px 30px rgba(0, 0, 0, 0.1);
    box-shadow: 0 4px 30px rgba(0, 0, 0, 0.1);
    backdrop-filter: blur(5px);
    -webkit-backdrop-filter: blur(5px);
    border: 1px solid rgba(255, 255, 255, 0.3);
}

.page-404-bg .text-error {
    color: #fff !important;
    text-shadow: rgba(255, 255, 255, 0.3) 5px 1px, rgba(255, 255, 255, 0.2) 10px 3px;
    font-size: 52px;
}
</style>
